import { useState } from 'react'
import { evaluate, round } from 'mathjs'
import { IValues, Tabs } from '@components/CalcAero/types'
import { retirementAges } from '@components/Cacl/data'
import { TGender } from '@components/Cacl/types'

const annuities = {
  1: 12,
  2: 23.539,
  3: 34.634,
  4: 45.302,
  5: 55.559,
  6: 65.422,
  7: 74.906,
  8: 84.025,
  9: 92.793,
  10: 101.224,
  11: 109.331,
  12: 117.126,
  13: 124.621,
  14: 131.828,
  15: 138.758,
  16: 145.421,
  17: 151.828,
  18: 157.989,
  19: 163.912,
  20: 169.608,
  21: 175.084,
  22: 180.35,
  23: 185.414,
  24: 190.283,
  25: 194.964,
}

const annualReturn = 0.075
const monthlyReturn = evaluate(`((${annualReturn} + 1) ^ ( 1 / 12)) - 1`)
const monthlyReturnRatio = evaluate(`${monthlyReturn} + 1`)

const quarterlyReturn = evaluate(`(${annualReturn} + 1) ^ 0.25 - 1`)
const quarterlyReturnRatio = evaluate(`${quarterlyReturn} + 1`)

const yearsInProgram = 5
const partOfAeroRatio = 0.2
const fee = 0.13

const useCalcAero = ({ gender, age, monthlyPayment, period }: IValues) => {
  const [genderVal, setGender] = useState<TGender>(gender)
  const [ageVal, setAge] = useState<number>(age)
  const [monthlyPaymentVal, setMonthlyPayment] = useState<number>(monthlyPayment)
  const [periodVal, setPeriod] = useState<number>(period)

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.capital)

  const numberOfYearsBeforeRetirement: number = evaluate(`${retirementAges[genderVal]} - ${ageVal}`)
  const quarterForInvest =
    activeTab === Tabs.capital
      ? evaluate(`${yearsInProgram} * 4 - 1`)
      : evaluate(`${numberOfYearsBeforeRetirement} * 4 - 1`)
  const numberOfMonths =
    activeTab === Tabs.capital ? evaluate(`${yearsInProgram} * 12`) : evaluate(`${numberOfYearsBeforeRetirement} * 12`)
  const inTotalYouWillAccumulate = round(
    evaluate(
      `${monthlyPaymentVal} * ${monthlyReturnRatio} * (
        ${monthlyReturnRatio} ^ ${numberOfMonths} - 1
      ) / (${monthlyReturnRatio} - 1)`,
    ),
    0,
  )
  const yourContributionsForFiveYears = evaluate(`${monthlyPaymentVal} * ${numberOfMonths}`)
  const yourProfit = evaluate(`${inTotalYouWillAccumulate} - ${yourContributionsForFiveYears}`)
  const aeroQuarterlyPayment = evaluate(`${partOfAeroRatio} * ${monthlyPaymentVal} * 3`)
  const inTotalYouWillAccumulateAero = round(
    evaluate(
      `${aeroQuarterlyPayment} * ${quarterlyReturnRatio} * (${quarterlyReturnRatio} ^ ${quarterForInvest} - 1) /
          (${quarterlyReturnRatio} - 1)`,
    ),
    0,
  )
  const contributionsForFiveYearsAero = Math.round(evaluate(`${quarterForInvest} * ${aeroQuarterlyPayment}`))
  const yourProfitAero = evaluate(`${inTotalYouWillAccumulateAero} - ${contributionsForFiveYearsAero}`)
  const yourContributionsAmount = evaluate(`${monthlyPaymentVal} * ${numberOfMonths}`)
  const personMonthlyPensionAmount = Math.round(evaluate(`${inTotalYouWillAccumulate} / ${periodVal} / 12`))
  const aeroMonthlyPensionAmount = Math.round(
    evaluate(`(${inTotalYouWillAccumulateAero} / ${annuities[periodVal as keyof typeof annuities]}) * (1 - ${fee})`),
  )
  const pensionCapital = evaluate(`${inTotalYouWillAccumulate} + ${inTotalYouWillAccumulateAero}`)
  const totalMonthlyPensionAmount = evaluate(`${personMonthlyPensionAmount} + ${aeroMonthlyPensionAmount}`)

  return {
    gender: genderVal,
    age: ageVal,
    monthlyPayment: monthlyPaymentVal,
    period: periodVal,
    setGender,
    setAge,
    setMonthlyPayment,
    setPeriod,
    activeTab,
    setActiveTab,
    pensionCapital,
    totalMonthlyPensionAmount,
    inTotalYouWillAccumulate,
    numberOfYearsBeforeRetirement,
    yourContributionsForFiveYears,
    yourProfit,
    yourContributionsAmount,
    inTotalYouWillAccumulateAero,
    contributionsForFiveYearsAero,
    yourProfitAero,
    aeroMonthlyPensionAmount,
  }
}

export default useCalcAero
